import type { FilterValidationOptions } from '../src/validate';
import type { FilterVariableEntry } from '../src/variables';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Filter, defFilter } from '../src/filter';
import type { FilterMetadata, Next, StepData } from '../src/types';

import {
  FilterSet,
  buildFilterSet,
  isFilterSet,
  isFilterSetArray,
} from '../src/filter-sets';
import { EmailValidationFn, getError } from '../src/validate';

export type FilterOps = {
  next: (
    current_step: string,
    next_steps: Next[],
    config?: FilterMetadata | undefined
  ) => void;
  set: (key: string, value: any) => void;
  toString: () => string;
  update: () => void;
};

export type FilterData = {
  id: string;
  type: string | null;
  steps: [string, StepData][];
  ops: FilterOps;
  hasMyRecordFilter: boolean;
  hasStageRecordFilter: boolean;
  hasVariableValue: boolean;
  hasAnyValidFiltersTypes: boolean;
  tooltip?: string;
};

export type FilterSetData = [string, { and: boolean; filters: FilterData[] }];

export type FilterSetsOps = {
  addFilter(setId: string): void;
  addFilterSet(): void;
  build(and: boolean): {
    and: boolean;
    query: {
      id: string;
      and: boolean;
      filters: {
        view_model: any[];
      }[];
    }[];
  };
  removeFilter(setId: string, index: number): void;
  reset(): void;
  updateVars(vars: FilterVariableEntry[]): void;
  setFilterSetOperation(setId: string, and: boolean): void;
  validate(
    options?: Pick<FilterValidationOptions, 'isEmptyFilterTypeInvalid'>
  ): ReturnType<typeof defFilterErrors>;
};

type FilterSetsMetadata = {
  filterCount: number;
  hasMyRecordFilter: boolean;
  hasStageRecordFilter: boolean;
  hasVariableValue: boolean;
  hasAnyValidFiltersTypes: boolean;
};

const defFilterErrors = (): {
  readonly hasErrors: boolean;
  errors: Record<string, (Record<string, string> | null)[]>;
} => {
  return {
    errors: {},
    get hasErrors() {
      return Object.values(this.errors).some((x) => x.some((y) => y !== null));
    },
  };
};

const buildFilterQuery = (
  filter: Filter | Filter[],
  { and = false, index = 0 } = {}
) => {
  const filters = Array.isArray(filter) ? filter : [filter];

  return {
    ...buildFilterSet({ and, filters }),
    id: `query-${index}`,
  };
};

const withoutNulls = <T>(arr: (T | null)[]) => {
  return arr.filter((x) => x !== null) as T[];
};

const opsForFilter = (
  filter: Filter,
  update: Dispatch<SetStateAction<any>>
) => ({
  next: (...args: Parameters<Filter['nextSteps']>) => {
    filter.nextSteps(...args);
  },
  set: (...args: Parameters<Filter['set']>) => {
    filter.set(...args);
  },
  toString: () => filter.toString(),
  update: () => update((prev: any) => ({ ...prev })),
  type: () => filter.type,
});

export const useFilterSets = (
  metadata: Record<string, any>,
  vars: FilterVariableEntry[],
  isValidPhoneNumber: (v: string) => boolean,
  isValidEmail: EmailValidationFn,
  init?: Filter | FilterSet | FilterSet[]
): [FilterSetData[], FilterSetsOps & FilterSetsMetadata] => {
  const { t } = useTranslation();
  const [initialSetId] = useState(uuidv4());
  const [setIds, setSetIds] = useState(() => {
    if (isFilterSetArray(init)) return init.map(() => uuidv4());
    return [initialSetId];
  });
  const [filters, setFilters] = useState<Record<string, Filter[]>>(() => {
    if (init instanceof Filter) return { [initialSetId]: [init] };
    if (isFilterSet(init)) return { [initialSetId]: init.filters };
    if (isFilterSetArray(init)) {
      return init.reduce<Record<string, Filter[]>>((acc, fs, idx) => {
        acc[setIds[idx]] = fs.filters;
        return acc;
      }, {});
    }
    return { [initialSetId]: [defFilter(metadata, { vars })] };
  });
  const [filterOps] = useState<Map<Filter, ReturnType<typeof opsForFilter>>>(
    () => {
      if (isFilterSetArray(init))
        return new Map(
          init
            .flatMap((x) => x.filters)
            .map((f) => [f, opsForFilter(f, setFilters)])
        );
      if (isFilterSet(init))
        return new Map(
          init.filters.map((f) => [f, opsForFilter(f, setFilters)])
        );
      if (init instanceof Filter)
        return new Map([[init, opsForFilter(init, setFilters)]]);

      const filter = filters[initialSetId][0];
      return new Map([[filter, opsForFilter(filter, setFilters)]]);
    }
  );
  const [filterIds] = useState<Map<Filter, string>>(() => {
    if (isFilterSetArray(init))
      return new Map(init.flatMap((x) => x.filters).map((f) => [f, uuidv4()]));
    if (isFilterSet(init))
      return new Map(init.filters.map((f) => [f, uuidv4()]));
    if (init instanceof Filter) return new Map([[init, uuidv4()]]);
    return new Map([[filters[initialSetId][0], uuidv4()]]);
  });
  const [setData] = useState(() => {
    if (isFilterSetArray(init))
      return new Map(init.map((x, idx) => [setIds[idx], { and: x.and }]));
    if (isFilterSet(init)) return new Map([[initialSetId, { and: init.and }]]);
    return new Map([[initialSetId, { and: false }]]);
  });

  const ops: Omit<FilterSetsOps, 'build' | 'validate'> = useMemo(() => {
    return {
      addFilter: (setId: string) => {
        setFilters((prev) => {
          const filter = defFilter(metadata, { vars });
          filterOps.set(filter, opsForFilter(filter, setFilters));
          const next = prev[setId] ? prev[setId].concat(filter) : [filter];
          return { ...prev, [setId]: next };
        });
      },
      addFilterSet: () => {
        const id = uuidv4();
        const filter = defFilter(metadata, { vars });
        filterOps.set(filter, opsForFilter(filter, setFilters));
        setSetIds((prev) => prev.concat(id));
        setFilters((prev) => ({ ...prev, [id]: [filter] }));
      },
      removeFilter: (setId: string, index: number) => {
        setFilters((prev) => {
          if (prev[setId].length === 1) {
            return Object.fromEntries(
              Object.entries(prev).filter(([key]) => key !== setId)
            );
          }
          return {
            ...prev,
            [setId]: prev[setId]
              .slice(0, index)
              .concat(prev[setId].slice(index + 1)),
          };
        });
      },
      reset: () => {
        const filter = defFilter(metadata, { vars });
        setSetIds([initialSetId]);
        setFilters({ [initialSetId]: [filter] });
        filterOps.clear();
        setData.clear();

        filterOps.set(filter, opsForFilter(filter, setFilters));
        setData.set(initialSetId, { and: false });
      },
      updateVars: (nextVars: FilterVariableEntry[]) => {
        setFilters((prev) => {
          Object.values(prev).forEach((fs) =>
            fs.forEach((filter) => filter.updateVars(nextVars))
          );
          return { ...prev };
        });
      },
      setFilterSetOperation: (setId: string, and: boolean) => {
        setData.set(setId, { ...setData.get(setId), and });
        setFilters((prev) => ({ ...prev }));
      },
    };
  }, [vars, initialSetId, setData, filterOps, metadata]);

  const build: FilterSetsOps['build'] = useCallback(
    (and = false) => {
      const fs = Object.entries(filters);

      const cleanedFilters = fs.reduce(
        (prev, current) => {
          const nextFilters = current[1].filter((f) => !!f.type);

          if (nextFilters.length) {
            prev.push([current[0], nextFilters]);
          }

          return prev;
        },
        [] as [string, Filter[]][]
      );

      const query = cleanedFilters.map(([setId, fs], idx) => {
        return buildFilterQuery(withoutNulls(fs), {
          and: setData.get(setId)?.and ?? false,
          index: idx,
        });
      });
      return { and, query };
    },
    [filters, setData]
  );

  const validate: FilterSetsOps['validate'] = useCallback(
    (options) => {
      const result = defFilterErrors();

      for (const [setId, fs] of Object.entries(filters)) {
        result.errors[setId] = [];

        for (const f of fs) {
          const error = getError(
            f,
            isValidPhoneNumber,
            isValidEmail,
            t,
            options
          );
          result.errors[setId].push(error);
        }
      }

      return result;
    },
    [filters, isValidPhoneNumber, isValidEmail, t]
  );

  const data: FilterSetData[] = useMemo(() => {
    return setIds
      .filter((id) => Boolean(filters[id]))
      .map((id) => {
        const filterData = filters[id].map((filter): FilterData => {
          const steps = [...filter];
          const ops = filterOps.get(filter)!;
          const id = filterIds.get(filter)!;
          const flags = filter.getFlags();
          return {
            id,
            type: filter.type,
            steps,
            ops,
            hasMyRecordFilter: flags.isMyRecords,
            hasStageRecordFilter: flags.isStageRecord,
            hasVariableValue: flags.hasVariableValue,
            hasAnyValidFiltersTypes: !!filter.type,
            tooltip: flags.tooltip,
          };
        });

        const set_data = setData.get(id) ?? { and: false };
        return [id, { ...set_data, filters: filterData }];
      });
  }, [filters, setIds, setData, filterOps, filterIds]);

  const {
    filterCount,
    hasMyRecordFilter,
    hasStageRecordFilter,
    hasVariableValue,
    hasAnyValidFiltersTypes,
  } = useMemo(() => {
    const allFilterData = data.flatMap(([, filter]) => filter.filters);
    const filterCount = allFilterData.reduce(
      (acc, data) => (data.type ? acc + 1 : acc),
      0
    );

    let hasMyRecordFilter = false;
    let hasStageRecordFilter = false;
    let hasVariableValue = false;
    let hasAnyValidFiltersTypes = false;

    for (const fd of allFilterData) {
      if (!hasMyRecordFilter) {
        hasMyRecordFilter = fd.hasMyRecordFilter;
      }

      if (!hasStageRecordFilter) {
        hasStageRecordFilter = fd.hasStageRecordFilter;
      }

      if (!hasVariableValue) {
        hasVariableValue = fd.hasVariableValue;
      }

      if (!hasAnyValidFiltersTypes) {
        hasAnyValidFiltersTypes = fd.hasAnyValidFiltersTypes;
      }
    }

    return {
      filterCount,
      hasMyRecordFilter,
      hasStageRecordFilter,
      hasVariableValue,
      hasAnyValidFiltersTypes,
    };
  }, [data]);

  return [
    data,
    {
      ...ops,
      build,
      validate,
      hasMyRecordFilter,
      hasStageRecordFilter,
      hasVariableValue,
      hasAnyValidFiltersTypes,
      filterCount,
    },
  ];
};

export type MetaFilterProps = {
  filterSets: FilterSetData[] | null;
  filterOps: (FilterSetsOps & FilterSetsMetadata) | null;
};
